import {
  CAPABILITIES,
  type Capability,
  capabilitiesIn,
  modelSaid,
  PRESETS,
  type Thinking,
} from "@rally/agreed";
import {
  Button,
  Col,
  Field,
  ScrollArea,
  Select,
  Stack,
  Surface,
  Switch,
  Text,
  useTheme,
} from "@rally/ui-kit";
import { useEffect, useState } from "react";
import { failureSaid } from "../localization/failure.ts";
import { say } from "../localization/say.ts";
import { act } from "../net/rally.ts";
import { FaceYouCanChange } from "./a-face-you-can-change.tsx";
import { modelsOnOffer } from "./a-provider.ts";
import {
  type AgentConnection,
  agentConnections,
  connectionId,
  connectionPayload,
  connectionStatus,
} from "./agent-connections.ts";
import type { Agent } from "./an-agent-row.tsx";
import { BriefingField } from "./briefing-field.tsx";
import { calledBy } from "./called.ts";
import { aFairEffortIn, effortOffered, effortSaid } from "./effort.ts";
import { Effort } from "./effort.tsx";
import { cannotSearchYet } from "./searching.ts";

const CALLED = {
  read: "agent.canRead",
  write: "agent.canWrite",
  shell: "agent.canShell",
  web: "agent.canWeb",
  subagents: "agent.canSubagents",
} as const satisfies Record<Capability, string>;

export function ConfigureAgent({
  agent,
  searchKeys,
  visible,
  onDismiss,
  onChanged,
}: {
  agent: Agent;

  searchKeys: number;
  visible: boolean;
  onDismiss: () => void;
  onChanged: (agent: Agent) => void;
}) {
  const theme = useTheme();
  const [providers, setProviders] = useState<AgentConnection[]>([]);
  const [credential, setCredential] = useState(connectionId(agent));
  const [model, setModel] = useState(agent.model);
  const chosen = providers.find((one) => one.id === credential);
  const status =
    connectionStatus(chosen, model) ??
    (agent.broker && credential === connectionId(agent)
      ? `${say("agents.brokerUnavailable")} · ${say("agents.brokerMeterUnknown")}`
      : null);
  const models = chosen ? modelsOnOffer(chosen) : [agent.model];
  const levels = effortOffered(model, chosen?.provider ?? agent.provider ?? undefined);
  const [thinking, setThinking] = useState<Thinking>(effortSaid(agent.thinking, levels));
  const [armed, setArmed] = useState<Capability[]>(agent.capabilities);
  const [definition, setDefinition] = useState(agent.definition ?? "");
  const [busy, setBusy] = useState(false);
  const [wrong, setWrong] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void agentConnections()
      .then((rows) => {
        if (active) setProviders(rows);
      })
      .catch((cause) => {
        if (active) setWrong(failureSaid(cause, say("agent.broke")));
      });
    return () => {
      active = false;
    };
  }, []);

  const arm = (capability: Capability, on: boolean) => {
    const next = CAPABILITIES.filter((one) => (one === capability ? on : armed.includes(one)));
    setArmed(next);
  };

  const startFrom = (name: string) => {
    const preset = PRESETS.find((one) => one.name === name);
    if (preset) setArmed(capabilitiesIn(preset.capabilities));
  };

  const save = async () => {
    setBusy(true);
    setWrong(null);
    try {
      const done = await act<Agent>("agent.edit", {
        account: agent.account,
        ...(credential !== connectionId(agent) ? connectionPayload(chosen, credential) : {}),
        ...(model !== agent.model ? { model } : {}),
        ...(thinking !== agent.thinking ? { thinking } : {}),
        ...(JSON.stringify(armed) !== JSON.stringify(agent.capabilities) ? { capabilities: armed } : {}),
        ...(definition !== (agent.definition ?? "") ? { definition } : {}),
      });
      onChanged(done);
      onDismiss();
    } catch (cause) {
      setWrong(failureSaid(cause, say("agent.broke")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Surface
      kind="panel"
      width={520}
      visible={visible}
      onDismiss={onDismiss}
      title={say("agent.configureTitle")(agent.handle)}
      testID="agent-configure-dialog"
      footer={
        <Button
          size="lg"
          loading={busy}
          disabled={!model}
          onPress={() => void save()}
          testID={`agent-configure-save-${agent.handle}`}
        >
          {say("agent.save")}
        </Button>
      }
    >
      <ScrollArea contentContainerStyle={{ padding: theme.space[4] }}>
        <Stack gap={3}>
          <Col style={{ alignItems: "center" }}>
            <FaceYouCanChange
              name={calledBy(agent) ?? agent.handle}
              avatar={agent.avatar}
              subject={agent.account}
              size={72}
              onChanged={() => onChanged(agent)}
              onWrong={(said) => setWrong(said)}
              testID={`agent-face-${agent.handle}`}
              removeTestID={`agent-drop-face-${agent.handle}`}
            />
            <Text typeName="caption" color="text.tertiary">
              {`@${agent.handle}`}
            </Text>
            <Text typeName="caption" color="text.tertiary">
              {say("agents.handleIsFixed")}
            </Text>
          </Col>
          <Field label={say("agents.provider")}>
            <Select
              testID="agent-configure-connection"
              value={credential}
              onChange={(id) => {
                setCredential(id);
                const picked = providers.find((one) => one.id === id);
                const offered = picked ? modelsOnOffer(picked) : [];
                const keep = picked?.broker && picked.provider === (chosen?.provider ?? agent.provider);
                const first = keep && offered.includes(model) ? model : offered[0];
                setModel(first ?? "");
                if (first && picked && (!keep || !effortOffered(first, picked.provider).includes(thinking)))
                  setThinking(aFairEffortIn(effortOffered(first, picked.provider)));
              }}
              options={[
                ...(!chosen
                  ? [
                      {
                        label: agent.broker
                          ? `${say("agents.brokerAccount")} · ${agent.broker.account ?? say("agents.automatic")} · ${agent.provider}`
                          : (agent.provider ?? agent.model),
                        value: connectionId(agent),
                      },
                    ]
                  : []),
                ...providers.map((one) => ({ label: one.label, value: one.id })),
              ]}
            />
          </Field>
          {status ? (
            <Text typeName="caption" color="text.secondary" testID="agent-broker-status">
              {status}
            </Text>
          ) : null}
          <Field label={say("agents.model")}>
            <Select
              testID="agent-configure-model"
              value={model}
              onChange={(id) => {
                setModel(id);
                setThinking(
                  aFairEffortIn(effortOffered(id, chosen?.provider ?? agent.provider ?? undefined)),
                );
              }}
              options={[
                ...(!models.includes(model) && model
                  ? [
                      {
                        label: say("agents.modelNoLongerOffered")(modelSaid(model)),
                        value: model,
                        disabled: true,
                      },
                    ]
                  : []),
                ...models.map((one) => ({ label: modelSaid(one), value: one })),
              ]}
            />
          </Field>
          <Effort offered={levels} value={thinking} onChange={setThinking} />
          <BriefingField value={definition} onChange={setDefinition} onPreset={(name) => startFrom(name)} />
          <Text typeName="caption" color="text.secondary">
            {say("agent.can")}
          </Text>
          {CAPABILITIES.map((capability) => (
            <Col key={capability} testID={`agent-can-${capability}`}>
              <Switch
                label={say(CALLED[capability])}
                value={armed.includes(capability)}
                onChange={(on) => arm(capability, on)}
              />
            </Col>
          ))}
          {cannotSearchYet({ capabilities: armed, searchKeys }) ? (
            <Text typeName="caption" color="warning" testID="agent-cannot-search">
              {say("agent.cannotSearchYet")}
            </Text>
          ) : null}
          {wrong ? (
            <Text typeName="caption" color="danger" testID="agent-configure-said">
              {wrong}
            </Text>
          ) : null}
        </Stack>
      </ScrollArea>
    </Surface>
  );
}
